/**
 * FinalScoreReviewTable — renders Step 22 final techno-commercial score review UI.
 *
 * Read-only transposed table: criteria as rows, vendors as columns. Shows technical
 * and commercial criteria sections with subtotals and a final score row.
 *
 * Author: Raj Kumar (raj.kumar@accellor.com)
 */
import React from "react";
import {
  Box,
  Paper,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MarkdownRenderer from "../../MarkdownRenderer";

type TechnicalCriterion = {
  criterion_name: string;
  score: number;
  max_score: number;
  weight: number;
  weighted_score: number;
  justification: string;
  evidence: string;
};

type FinalVendorScore = {
  vendor_name: string;
  rank: number | null;
  final_score: number;
  qualified: boolean;
  scores: {
    technical: { criteria: TechnicalCriterion[]; total: number };
    commercial: { criteria: TechnicalCriterion[]; total: number };
    overall: { final_score: number };
  };
};

type FinalScoreReviewTableProps = {
  vendors: FinalVendorScore[];
  technicalWeight: number;
  commercialWeight: number;
  winner: string;
  recommendation: string;
  message: string;
  isSubmitting: boolean;
  hasApproved: boolean;
  onApprove: () => void;
};

const SECTION_STYLE = {
  backgroundColor: "#e8f5e9",
  fontWeight: 700,
  fontSize: "13px",
  color: "#1b5e20",
  padding: "10px 16px",
  borderBottom: "1px solid #c8e6c9",
  borderRight: "1px solid #e0e0e0",
};

const COMMERCIAL_SECTION_STYLE = {
  backgroundColor: "#e3f2fd",
  fontWeight: 700,
  fontSize: "13px",
  color: "#0d47a1",
  padding: "10px 16px",
  borderBottom: "1px solid #bbdefb",
  borderRight: "1px solid #e0e0e0",
};

const SUBTOTAL_STYLE = {
  backgroundColor: "#f5f5f5",
  fontWeight: 700,
  fontSize: "13px",
  color: "#333",
  padding: "12px 16px",
  borderBottom: "2px solid #e0e0e0",
  borderRight: "1px solid #e0e0e0",
};

const FINAL_SCORE_STYLE = {
  backgroundColor: "#004141",
  fontWeight: 700,
  fontSize: "14px",
  color: "#ffffff",
  padding: "14px 16px",
  borderRight: "1px solid #00695c",
};

export default function FinalScoreReviewTable({
  vendors,
  technicalWeight,
  commercialWeight,
  winner,
  recommendation,
  message,
  isSubmitting,
  hasApproved,
  onApprove,
}: FinalScoreReviewTableProps): JSX.Element {
  if (vendors.length === 0) return <></>;

  const commCriteria = vendors[0].scores?.commercial?.criteria ?? [];

  const getButtonLabel = () => {
    if (hasApproved) return "Approved";
    if (isSubmitting) return "Approving...";
    return "Approve & Export";
  };

  const scoreCell = (value: number | null | undefined, extra?: React.ReactNode) => (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
      <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
        {value !== null && value !== undefined ? value.toFixed(1) : "—"}
      </Typography>
      {extra}
    </Box>
  );

  return (
    <>
      {message && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              "& h1": { fontSize: "20px", fontWeight: 600, color: "#004141", mb: 2 },
              "& h2": { fontSize: "16px", fontWeight: 600, color: "#004141", mb: 1.5, mt: 2 },
              "& p": { margin: "8px 0" },
              "& strong": { fontWeight: 600, color: "#004141" },
              "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
              "& li": { marginBottom: "4px" },
              "& hr": { border: "none", borderTop: "1px solid #b2e5de", margin: "16px 0" },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Weight badges */}
      {(technicalWeight > 0 || commercialWeight > 0) && (
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Chip
            label={`Technical: ${technicalWeight}%`}
            size="small"
            sx={{ backgroundColor: "#e8f5e9", color: "#1b5e20", fontWeight: 600, fontSize: "12px" }}
          />
          <Chip
            label={`Commercial: ${commercialWeight}%`}
            size="small"
            sx={{ backgroundColor: "#e3f2fd", color: "#0d47a1", fontWeight: 600, fontSize: "12px" }}
          />
        </Box>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ mb: 3, border: "1px solid #e0e0e0", borderRadius: "4px", overflow: "hidden" }}
      >
        <Table sx={{ borderCollapse: "collapse" }}>
          <TableHead>
            {/* Row 1: Criteria header + vendor name headers */}
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  backgroundColor: "#f5f5f5",
                  fontWeight: 600,
                  color: "#333",
                  fontSize: "14px",
                  padding: "16px",
                  borderBottom: "2px solid #e0e0e0",
                  borderRight: "1px solid #e0e0e0",
                  minWidth: 220,
                  verticalAlign: "middle",
                }}
              >
                Criteria
              </TableCell>
              {vendors.map((vendor, idx) => (
                <TableCell
                  key={idx}
                  colSpan={2}
                  align="center"
                  sx={{
                    backgroundColor: vendor.qualified ? "#c8e6c9" : "#ffcdd2",
                    fontWeight: 600,
                    color: "#333",
                    fontSize: "14px",
                    padding: "16px",
                    borderBottom: "1px solid #e0e0e0",
                    borderRight: idx < vendors.length - 1 ? "1px solid #e0e0e0" : "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    {vendor.vendor_name}
                    {vendor.rank !== null && (
                      <Chip
                        label={`#${vendor.rank}`}
                        size="small"
                        sx={{ backgroundColor: "#ffffff", color: "#333", fontWeight: 700, fontSize: "11px", height: "20px" }}
                      />
                    )}
                    {vendor.qualified && <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 18 }} />}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            {/* Row 2: Sub-headers Score / Weighted Score per vendor */}
            <TableRow>
              {vendors.map((vendor, idx) => (
                <React.Fragment key={idx}>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: vendor.qualified ? "#e8f5e9" : "#ffebee",
                      fontWeight: 600,
                      color: "#333",
                      fontSize: "12px",
                      padding: "10px 16px",
                      borderBottom: "2px solid #e0e0e0",
                      borderRight: "1px solid #e0e0e0",
                      minWidth: 80,
                    }}
                  >
                    Score
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: vendor.qualified ? "#e8f5e9" : "#ffebee",
                      fontWeight: 600,
                      color: "#333",
                      fontSize: "12px",
                      padding: "10px 16px",
                      borderBottom: "2px solid #e0e0e0",
                      borderRight: idx < vendors.length - 1 ? "1px solid #e0e0e0" : "none",
                      minWidth: 100,
                    }}
                  >
                    Weighted
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Commercial section header */}
            <TableRow>
              <TableCell sx={COMMERCIAL_SECTION_STYLE}>Commercial</TableCell>
              {vendors.map((_, idx) => (
                <React.Fragment key={idx}>
                  <TableCell sx={{ ...COMMERCIAL_SECTION_STYLE, borderRight: "1px solid #e0e0e0" }} />
                  <TableCell
                    sx={{
                      ...COMMERCIAL_SECTION_STYLE,
                      borderRight: idx < vendors.length - 1 ? "1px solid #e0e0e0" : "none",
                    }}
                  />
                </React.Fragment>
              ))}
            </TableRow>

            {/* Commercial criteria rows */}
            {commCriteria.map((criterion, cIdx) => (
              <TableRow key={`comm-${cIdx}`} sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                <TableCell
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#333",
                    padding: "14px 16px",
                    borderBottom: "1px solid #e0e0e0",
                    borderRight: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Box>
                    {criterion.criterion_name}
                    <Typography sx={{ fontSize: "11px", color: "#666", mt: 0.25 }}>
                      Weight: {criterion.weight}%
                    </Typography>
                  </Box>
                </TableCell>
                {vendors.map((vendor, vIdx) => {
                  const c = vendor.scores?.commercial?.criteria?.find(
                    (x) => x.criterion_name === criterion.criterion_name
                  );
                  return (
                    <React.Fragment key={vIdx}>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: "13px",
                          padding: "14px 16px",
                          borderBottom: "1px solid #e0e0e0",
                          borderRight: "1px solid #e0e0e0",
                        }}
                      >
                        {c ? (
                          <Tooltip title={c.justification || ""} arrow placement="top">
                            <Box sx={{ cursor: "help" }}>
                              {scoreCell(c.max_score ? (c.score / c.max_score) * 100 : c.score)}
                            </Box>
                          </Tooltip>
                        ) : "—"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: "13px",
                          padding: "14px 16px",
                          borderBottom: "1px solid #e0e0e0",
                          borderRight: vIdx < vendors.length - 1 ? "1px solid #e0e0e0" : "none",
                        }}
                      >
                        {c ? scoreCell(c.weighted_score) : "—"}
                      </TableCell>
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}

            {/* Commercial subtotal row */}
            <TableRow>
              <TableCell sx={SUBTOTAL_STYLE}>Commercial Subtotal</TableCell>
              {vendors.map((vendor, idx) => (
                <React.Fragment key={idx}>
                  <TableCell
                    align="center"
                    sx={{ ...SUBTOTAL_STYLE, borderRight: "1px solid #e0e0e0" }}
                  />
                  <TableCell
                    align="center"
                    sx={{
                      ...SUBTOTAL_STYLE,
                      borderRight: idx < vendors.length - 1 ? "1px solid #e0e0e0" : "none",
                    }}
                  >
                    {(vendor.scores?.commercial?.total ?? 0).toFixed(1)}
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>

            {/* Final Score row */}
            <TableRow>
              <TableCell sx={FINAL_SCORE_STYLE}>Final Score</TableCell>
              {vendors.map((vendor, idx) => (
                <React.Fragment key={idx}>
                  <TableCell
                    align="center"
                    sx={{ ...FINAL_SCORE_STYLE, borderRight: "1px solid #00695c" }}
                  />
                  <TableCell
                    align="center"
                    sx={{
                      ...FINAL_SCORE_STYLE,
                      borderRight: idx < vendors.length - 1 ? "1px solid #00695c" : "none",
                      fontSize: "16px",
                    }}
                  >
                    {(vendor.scores?.overall?.final_score ?? vendor.final_score ?? 0).toFixed(1)}%
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Winner + Recommendation */}
      {(winner || recommendation) && (
        <Paper
          elevation={0}
          sx={{ p: 2.5, mb: 3, backgroundColor: "#f0fbf9", border: "1px solid #b2e5de", borderRadius: "8px" }}
        >
          {winner && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: recommendation ? 1 : 0 }}>
              <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
              <Typography component="span" sx={{ fontSize: "14px", fontWeight: 600, color: "#004141", my: 0 }}>
                Recommended: {winner}
              </Typography>
            </Box>
          )}
          {recommendation && (
            <Typography sx={{ fontSize: "13px", color: "#2a312b" }}>{recommendation}</Typography>
          )}
        </Paper>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={onApprove}
          disabled={isSubmitting || hasApproved}
          sx={{
            backgroundColor: "#00a888",
            "&:hover": { backgroundColor: "#008f75" },
            "&.Mui-disabled": { backgroundColor: "#ccc", color: "#666" },
            textTransform: "none",
            fontWeight: 600,
            px: 4,
          }}
        >
          {getButtonLabel()}
        </Button>
      </Box>
    </>
  );
}
